/**
 * Navbar Component - Modern Clean Design
 * Top navigation bar with minimal, professional styling
 */

import React, { useState, forwardRef, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Badge,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import KeyboardShortcutHelper, { KeyboardShortcutHelperRef } from './KeyboardShortcutHelper';
import { tokens } from '../../theme/tokens';
import { batchService } from '../../services/api/batch.service';

interface NavbarProps {
  onMenuClick: () => void;
  shortcutHelperRef?: React.RefObject<KeyboardShortcutHelperRef>;
}

const StyledAppBar = styled(AppBar)({
  background: tokens.colors.surface.glass,
  backdropFilter: 'blur(20px) saturate(180%)',
  boxShadow: tokens.shadows.sm,
  borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
  position: 'fixed',
  zIndex: 1200,
  transition: `all ${tokens.transitions.duration.normal} ${tokens.transitions.easing.standard}`,
});

const StyledBadge = styled(Badge)({
  '& .MuiBadge-badge': {
    backgroundColor: tokens.colors.semantic.error,
    color: tokens.colors.text.inverse,
    fontWeight: 600,
    fontSize: '10px',
    height: '18px',
    minWidth: '18px',
    boxShadow: '0 0 0 2px #FFFFFF',
    animation: 'pulse 2s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.7,
    },
  },
});

const UserAvatar = styled(Avatar)({
  width: 36,
  height: 36,
  fontSize: '14px',
  fontWeight: 600,
  border: `2px solid ${tokens.colors.neutral[100]}`,
  background: `linear-gradient(135deg, ${tokens.colors.primary[100]} 0%, ${tokens.colors.primary[200]} 100%)`,
  color: tokens.colors.primary[700],
  transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.easing.standard}`,
  cursor: 'pointer',
  '&:hover': {
    borderColor: tokens.colors.primary.main,
    transform: 'scale(1.05)',
    boxShadow: tokens.shadows.purple,
  },
});

const Navbar: React.FC<NavbarProps> = ({ onMenuClick, shortcutHelperRef }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [heldBatches, setHeldBatches] = useState<{ id: string; batchNumber: string }[]>([]);
  const open = Boolean(anchorEl);

  // Poll for PARTIALLY_APPROVED batches as notifications
  useEffect(() => {
    const load = async () => {
      try {
        const list = await batchService.getBatchList({ status: 'PARTIALLY_APPROVED' });
        setHeldBatches(
          (list.batches ?? []).map((b: any) => ({
            id: b.batchId,
            batchNumber: b.batchNumber,
          }))
        );
      } catch { /* silent */ }
    };
    load();
    const timer = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(timer);
  }, []);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  return (
    <StyledAppBar position="fixed" elevation={0}>
      <Toolbar sx={{ minHeight: '64px !important', px: 3 }}>
        {/* Menu Icon */}
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={onMenuClick}
          sx={{ 
            mr: 2,
            width: 40,
            height: 40,
            color: tokens.colors.neutral[500],
            '&:hover': {
              backgroundColor: tokens.colors.neutral[100],
              color: tokens.colors.neutral[800],
            },
            transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.easing.standard}`,
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Provana Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box
            component="img"
            src="/assets/provana-logo.png"
            alt="Provana"
            sx={{
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                opacity: 0.85,
              },
            }}
          />
        </Box>

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* User info */}
          <Box 
            sx={{ 
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'flex-end',
              mr: 2,
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: tokens.colors.text.primary, 
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: 1.2,
              }}
            >
              {user?.name}
            </Typography>

          </Box>

          {/* Keyboard Shortcuts Helper */}
          <KeyboardShortcutHelper ref={shortcutHelperRef} />

          {/* Notifications */}
          <Tooltip title="Notifications" arrow>
            <IconButton
              onClick={(e) => setNotifAnchor(e.currentTarget)}
              aria-label={heldBatches.length > 0 ? `Notifications — ${heldBatches.length} held batch${heldBatches.length === 1 ? '' : 'es'}` : 'Notifications — no new items'}
              aria-haspopup="true"
              aria-expanded={Boolean(notifAnchor) ? 'true' : undefined}
              sx={{
                width: 40, height: 40, color: '#6B7280',
                '&:hover': { backgroundColor: '#F3F4F6', color: '#1F2937' },
              }}
            >
              <StyledBadge badgeContent={heldBatches.length || 0} max={99}>
                <NotificationsIcon fontSize="small" />
              </StyledBadge>
            </IconButton>
          </Tooltip>

          {/* Notification Popover */}
          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { width: 360, mt: 1, borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.14)', overflow: 'hidden' } }}
          >
            {/* Header */}
            <Box sx={{ px: 2.5, py: 1.5, background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', borderBottom: '1px solid #FCD34D', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon sx={{ color: '#D97706', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400E' }}>Notifications</Typography>
                {heldBatches.length > 0 && (
                  <Box sx={{ ml: 0.5, px: 1, py: 0.25, bgcolor: '#EA580C', borderRadius: 10, display: 'inline-flex' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{heldBatches.length}</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {heldBatches.length === 0 ? (
              <Box sx={{ px: 2.5, py: 3, textAlign: 'center', background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)' }}>
                <Typography variant="body2" sx={{ color: '#065F46', fontWeight: 600 }}>✅ All clear — no held allocations</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {heldBatches.map((b, i) => (
                  <ListItem
                    key={b.id}
                    button
                    onClick={() => {
                      setNotifAnchor(null);
                      navigate(`/allocation/matrix?batchId=${b.id}`);
                    }}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderBottom: i < heldBatches.length - 1 ? '1px solid #FDE68A' : 'none',
                      background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
                      cursor: 'pointer',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 38 }}>
                      <PauseCircleIcon sx={{ color: '#F59E0B', fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#92400E', fontSize: 13 }}>
                          Held allocations — {b.batchNumber}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: '#B45309' }}>
                          Manager held procedures. Click to fix &amp; resubmit.
                        </Typography>
                      }
                    />
                    <Box sx={{ ml: 1 }}>
                      <Box component="span" sx={{ fontSize: 11, px: 1, py: 0.5, bgcolor: '#F59E0B', color: '#fff', borderRadius: 8, fontWeight: 700 }}>
                        Fix
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Popover>

          {/* User Profile */}
          <IconButton
            onClick={handleProfileClick}
            aria-label={`Account menu for ${user?.name ?? 'user'}`}
            sx={{ 
              ml: 1,
              p: 0,
            }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            {user?.profilePhoto ? (
              <UserAvatar
                src={user.profilePhoto}
                alt={user.name}
              />
            ) : (
              <UserAvatar sx={{ bgcolor: '#3B82F6' }}>
                {user?.name.charAt(0).toUpperCase()}
              </UserAvatar>
            )}
          </IconButton>
        </Box>
      </Toolbar>

      {/* Profile Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            border: '1px solid #E5E7EB',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Section */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827' }}>
            {user?.name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            {user?.email}
          </Typography>
        </Box>

        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: '#EF4444' }} />
          </ListItemIcon>
          <Typography sx={{ color: '#EF4444' }}>Logout</Typography>
        </MenuItem>
      </Menu>
    </StyledAppBar>
  );
};

export default Navbar;
